import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { SystemVersion } from '@magnetar/admin-api-interfaces'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { logger } from '@magnetar/utils'
import { Settings } from 'lucide-react'
import { SystemVersionEditDialog } from './SystemVersionEditDialog'
import { DeleteAlert } from './DeleteAlert'
import { uploadFileService } from '@/services/upload_file_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

type TableMeta = {
  reloadData: () => void
}

export const columns: ColumnDef<SystemVersion>[] = [
  {
    accessorKey: 'version',
    header: '版本號',
  },
  {
    accessorKey: 'min_backend_version',
    header: '最低後端版本',
  },
  {
    accessorKey: 'file_source',
    header: '檔案來源',
  },
  {
    accessorKey: 'memo',
    header: '備註',
  },
  {
    accessorKey: 'is_active',
    header: '狀態',
    cell: ({ row }) => (
      <Badge variant={row.original.is_active ? 'default' : 'secondary'}>
        {row.original.is_active ? '啟用' : '停用'}
      </Badge>
    ),
  },
  {
    accessorKey: 'created_at',
    header: '建立時間',
    cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const systemVersion = row.original
      const [showEditDialog, setShowEditDialog] = useState(false)
      const [showDeleteAlert, setShowDeleteAlert] = useState(false)
      const meta = table.options.meta as TableMeta
      const { token } = useAuth()

      const handleDownload = async () => {
        try {
          // 使用 Signed URL 下載（直連 GCS，無大小限制）
          toast.info('正在準備下載...')
          const downloadUrl = await uploadFileService.getFileDownloadUrl(
            systemVersion.file_source,
            token,
          )
          window.open(downloadUrl, '_blank')
          toast.success('下載已開始')
        } catch (error) {
          logger.error('下載失敗:', error)
          toast.error('下載失敗，請稍後再試')
        }
      }

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setShowEditDialog(true)}>
                編輯基本資料
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleDownload}>
                下載檔案
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onSelect={() => setShowDeleteAlert(true)}
              >
                刪除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <SystemVersionEditDialog
            systemVersion={systemVersion}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            onSuccess={meta.reloadData}
          />

          <DeleteAlert
            systemVersion={systemVersion}
            open={showDeleteAlert}
            onOpenChange={setShowDeleteAlert}
            onSuccess={meta.reloadData}
          />
        </>
      )
    },
  },
]
